import * as logger from "../../utils/logger";
import { versionComparator } from "../helpers";
import { handleIoxConnection } from "./ioxManager";
import { handleBackgroundMode } from "./nativeBackgroundModeHandler";
import { addNativeEventListeners } from "./nativeEventHandler";
import { AppType, getAppType, isNativeApp } from "./nativeHelpers";
import { handleUserActions } from "./userActionHandler";

let appVersion = null;
let isSdkInitialized = false;

type ErrorCallBack = (error: Error, _tags: string[]) => void;

export function getServerAddress() {
    let loc = window.location;
    let serverAddress = `${ loc.hostname }${ loc.port ? `:${ loc.port }` : "" }`;
    return serverAddress;
}

/**
 * Set last-server update callback
 */
let notifyLastServerUpdate = function () {
    let serverAddress = getServerAddress();
    switch (getAppType()) {
        case AppType.CordovaApp:
            if (self !== top) {
                //not used currently. Original code is at drive/default.ts
                window.parent.postMessage({ type: "drive:last-server", server: serverAddress }, "file:///");
            }
            break;
        case AppType.NativeSdk:
            window.geotabModules?.app.updateLastServer(serverAddress, error => {
                if (error) {
                    logger.error(logger.Source.NATIVE, `lastServerUpdate: Error: Name: ${ error.name }, Message: ${ error.message }, Stack: ${ error.stack }`);
                }
            });
            break;
        case AppType.WebApp:
        default:
            break;
    }
};

/**
 * Additional plugins should only be listened to after deviceready
 */
let initiateNativeDevice = function (errorCallback?: ErrorCallBack) {
    try {
        notifyLastServerUpdate();
    } catch (error) {
        errorCallback && errorCallback(error, ["nativeUnhandledException"]);
    }
    addNativeEventListeners();
    try {
        handleBackgroundMode();
    } catch (error) {
        errorCallback && errorCallback(error, ["nativeUnhandledException"]);
    }
    try {
        handleIoxConnection();
    } catch (error) {
        errorCallback && errorCallback(error, ["nativeUnhandledException"]);
    }

    if (isNativeApp() && window.DRIVE_DEBUG) {
        // Disable this flag inside native app
        window.DRIVE_DEBUG.DISABLE_AUTO_DRIVE_ACTIONS = false;
    }
};

let handleError = function (version: string, error: Error, errorCallback?: ErrorCallBack) {
    const isErrorFromJavaInvocation = error.message.includes("Java exception was raised during method invocation");

    const isOlderVersion = versionComparator(window.device.version, version) < 0;
    if (!isErrorFromJavaInvocation || !isOlderVersion) {
        errorCallback && errorCallback(error, ["nativeUnhandledException"]);
    }
};

export function initializeCordovaDevice(errorCallback?: ErrorCallBack) {
    if (!window.cordova || !window.device) {
        return false;
    }
    window.device.appType = AppType.CordovaApp;

    initiateNativeDevice(errorCallback);

    try {
        window.cordova.getAppVersion &&
            window.cordova.getAppVersion(function (version) {
                appVersion = version;
            });
    } catch (error) {
        errorCallback && errorCallback(error, ["nativeUnhandledException"]);
    }

    if (self !== top) {
        window.parent.postMessage({ type: "drive:listening" }, "file:///*");
    }

    logger.log(logger.Source.NATIVE, "Initialized Cordova");
    return true;
}

export function initializeNativeSDK(errorCallback?: ErrorCallBack) {
    // We need to check for isSdkInitialized in addition to window.geotabmodules because it's possible for
    // this function to be called in both the deviceready callback AND just inside the require() callback
    // in main.ts -- in other words, window.geotabModules might be present in both function call
    // scenarios, but only ever want this function called once
    // window.DRIVE_DEBUG.TEST_MODE is to ignore "isSDKInitialized" value in testing
    if ((!(window.DRIVE_DEBUG && window.DRIVE_DEBUG.TEST_MODE) && isSdkInitialized) || !window.geotabModules) {
        return;
    }
    isSdkInitialized = true;
    window.device = window.geotabModules.device;
    window.device.appType = AppType.NativeSdk;
    initiateNativeDevice(errorCallback);
    handleUserActions();
    try {
        window.geotabModules.connectivity.start(undefined, () => {});
    } catch (error) {
        /**
         * Ignoring "Java exception was raised during method invocation" from older drive apps ( version < 6.5.0)
         * Since it is creating excessive noise on sentry
         * See MYG-61547 for details
         */
        handleError("6.5.0", error as Error, errorCallback);
    }
    logger.log(logger.Source.NATIVE, "Initialized SDK");
}

/**
 * Get our native application version.
 * For NativeSdk (DriveNative), prefers window.device.displayVersion which is
 * constructed by the native shell to include the OTA update identifier when
 * applicable (e.g. "7.0.0_7362 (OTA 72d14f5)"). Falls back to window.device.version
 * for builds that do not yet carry displayVersion.
 */
export function getVersion(): string | null {
    switch (getAppType()) {
        case AppType.CordovaApp:
            return appVersion;
        case AppType.NativeSdk:
            return window.device.displayVersion ?? window.device.version;
        case AppType.WebApp:
        default:
            return null;
    }
}
